import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import ExtendedBadge from "@/components/ExtendedBadge";
import type { ApprovalInspectionPhase } from "../../../services/applications-services";

/**
 * The inspection actually recorded against this application.
 *
 * Every value comes from `inspectionPhase` of the approval-status response
 * (`UnifiedApprovalDTO.InspectionPhaseDTO` -> `InspectionDTO`). When the
 * backend has no inspection for the application, this renders an empty state —
 * it never stands in placeholder plots, inspectors, dates or conditions.
 */

function hasText(value: string | null | undefined): value is string {
    return typeof value === "string" && value.trim() !== "";
}

function humanize(value: string | null | undefined): string | undefined {
    if (!hasText(value)) return undefined;
    if (/[a-z]/.test(value)) return value;
    return value
        .toLowerCase()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

function formatDate(value: string | null | undefined): string | undefined {
    if (!hasText(value)) return undefined;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function cleanList(items: (string | null | undefined)[] | null | undefined): string[] {
    return (items ?? []).filter(hasText).map((item) => item.trim());
}

function DetailCell({ label, value }: { label: string; value: string }) {
    return (
        <div className="p-4">
            <p className="text-gray-600 mb-1">{label}</p>
            <p className="font-semibold text-[#3C1E03]">{value}</p>
        </div>
    );
}

export type InvestmentInspectionPanelProps = {
    inspectionPhase?: ApprovalInspectionPhase | null;
    isLoading?: boolean;
    isError?: boolean;
};

export default function InvestmentInspectionPanel({
    inspectionPhase,
    isLoading,
    isError,
}: InvestmentInspectionPanelProps) {
    return (
        <div>
            <h1 className="my-2 text-lg font-semibold text-[#3C1E03]">Investment Inspection</h1>
            <div className="bg-white rounded-lg p-6 shadow-sm">
                <InspectionBody
                    inspectionPhase={inspectionPhase}
                    isLoading={isLoading}
                    isError={isError}
                />
            </div>
        </div>
    );
}

function InspectionBody({ inspectionPhase, isLoading, isError }: InvestmentInspectionPanelProps) {
    if (isLoading) {
        return (
            <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                Loading inspection details…
            </div>
        );
    }

    if (isError) {
        return (
            <p className="text-sm text-gray-600">
                We could not load the inspection details for this application right now. Please try again later.
            </p>
        );
    }

    const inspection = inspectionPhase?.inspection;
    const areas = (inspection?.areasOfInspection ?? []).filter(Boolean);
    const violations = cleanList(inspection?.violations);
    const recommendations = cleanList(inspection?.recommendations);
    const inspectors = cleanList(inspection?.inspectorNames);
    const feedback = inspectionPhase?.comment ?? inspection?.feedback;
    const scheduledDate = formatDate(inspection?.scheduledDate);
    const completedDate = formatDate(inspection?.completedDate ?? inspectionPhase?.inspectionDate);
    const status = humanize(inspection?.status ?? inspectionPhase?.simpleStatus ?? inspectionPhase?.status);
    const decision = inspectionPhase?.finalDecision;

    const hasAnything =
        !!inspection?.id ||
        areas.length > 0 ||
        violations.length > 0 ||
        recommendations.length > 0 ||
        inspectors.length > 0 ||
        hasText(feedback) ||
        !!scheduledDate ||
        !!completedDate ||
        !!decision;

    if (!hasAnything) {
        return (
            <p className="text-sm text-gray-600">
                No inspection has been recorded for this application yet. Once ZCT schedules and carries
                out an inspection, its date, inspectors and findings will appear here.
            </p>
        );
    }

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                    <h2 className="text-lg font-bold text-[#2C2C2C]">
                        {humanize(inspection?.type) ?? "Inspection"}
                    </h2>
                    {hasText(inspection?.location) && (
                        <p className="text-gray-600 mt-1">{inspection?.location}</p>
                    )}
                </div>
                {status && <ExtendedBadge variant="primary" label={status} />}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 border rounded-lg overflow-hidden divide-y sm:divide-y-0 sm:divide-x">
                {scheduledDate && <DetailCell label="Scheduled for" value={scheduledDate} />}
                {completedDate && <DetailCell label="Completed on" value={completedDate} />}
                {inspectors.length > 0 && (
                    <DetailCell
                        label={inspectors.length > 1 ? "Inspectors" : "Inspector"}
                        value={inspectors.join(", ")}
                    />
                )}
            </div>

            {hasText(inspection?.description) && (
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Scope</p>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-700">
                        {inspection?.description}
                    </p>
                </div>
            )}

            {hasText(feedback) && (
                <div className="rounded-lg border border-secondary-100 bg-[#f5f2f0] p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Inspector's feedback
                    </p>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{feedback}</p>
                </div>
            )}

            {areas.length > 0 && (
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Areas inspected
                    </p>
                    <div className="mt-2 space-y-2">
                        {areas.map((area, index) => {
                            const findings = cleanList(area.findings);
                            return (
                                <div
                                    key={area.id ?? index}
                                    className="rounded-lg border border-secondary-100 p-3"
                                >
                                    <div className="flex items-center gap-2">
                                        {area.passed === true && (
                                            <CheckCircle2 className="size-4 text-green-600" />
                                        )}
                                        {area.passed === false && (
                                            <XCircle className="size-4 text-red-600" />
                                        )}
                                        <span className="font-medium text-[#2C2C2C]">
                                            {area.name ?? `Area ${index + 1}`}
                                        </span>
                                    </div>
                                    {hasText(area.comments) && (
                                        <p className="mt-1 whitespace-pre-line text-sm text-gray-700">
                                            {area.comments}
                                        </p>
                                    )}
                                    {findings.length > 0 && (
                                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
                                            {findings.map((finding, findingIndex) => (
                                                <li key={findingIndex}>{finding}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {violations.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-red-700">
                        <AlertTriangle className="size-3.5" /> Violations recorded
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-red-800">
                        {violations.map((violation, index) => (
                            <li key={index}>{violation}</li>
                        ))}
                    </ul>
                </div>
            )}

            {recommendations.length > 0 && (
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Recommendations
                    </p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
                        {recommendations.map((recommendation, index) => (
                            <li key={index}>{recommendation}</li>
                        ))}
                    </ul>
                </div>
            )}

            {decision && (
                <div className="border-t border-secondary-100 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Decision on this inspection
                    </p>
                    {humanize(decision.simpleStatus ?? decision.status) && (
                        <p className="mt-1 font-semibold text-[#2C2C2C]">
                            {humanize(decision.simpleStatus ?? decision.status)}
                        </p>
                    )}
                    {(hasText(decision.decidedBy) || formatDate(decision.decisionDate)) && (
                        <p className="mt-1 text-sm text-gray-600">
                            {hasText(decision.decidedBy) && <>Decided by {decision.decidedBy}</>}
                            {hasText(decision.decidedBy) && formatDate(decision.decisionDate) && " · "}
                            {formatDate(decision.decisionDate) && (
                                <>on {formatDate(decision.decisionDate)}</>
                            )}
                        </p>
                    )}
                    {hasText(decision.comment) && (
                        <p className="mt-2 whitespace-pre-line text-sm text-gray-700">
                            {decision.comment}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
